"use client";

import { useCallback, useSyncExternalStore } from "react";
import {
    isPaymentCreditProfileId,
    isPaymentLoanTerm,
    PAYMENT_PREFERENCES_STORAGE_KEY,
    type PaymentCreditProfileId,
    type PaymentLoanTerm,
    type StoredPaymentPreferences
} from "@/utils/paymentEstimate";

const CHANGE_EVENT = "thiscar-payment-preferences-change";

let cachedRaw: string | null = null;
let cachedPreferences: StoredPaymentPreferences | null = null;

function parsePreferences(raw: string | null): StoredPaymentPreferences | null {
    if (!raw) return null;

    try {
        const parsed = JSON.parse(raw) as Partial<StoredPaymentPreferences> | null;
        if (!parsed || typeof parsed !== "object") return null;

        const down = Number(parsed.down);
        if (!Number.isFinite(down) || down < 0) return null;
        if (!isPaymentLoanTerm(parsed.term)) return null;
        if (!isPaymentCreditProfileId(parsed.tier)) return null;

        return {
            down,
            term: parsed.term,
            tier: parsed.tier,
            customized: Boolean(parsed.customized)
        };
    } catch {
        return null;
    }
}

function getSnapshot(): StoredPaymentPreferences | null {
    let raw: string | null = null;
    try {
        raw = window.localStorage.getItem(PAYMENT_PREFERENCES_STORAGE_KEY);
    } catch {
        raw = null;
    }

    if (raw !== cachedRaw) {
        cachedRaw = raw;
        cachedPreferences = parsePreferences(raw);
    }

    return cachedPreferences;
}

function getServerSnapshot(): StoredPaymentPreferences | null {
    return null;
}

function subscribe(onStoreChange: () => void): () => void {
    const handleStorage = (event: StorageEvent) => {
        if (event.key === null || event.key === PAYMENT_PREFERENCES_STORAGE_KEY) onStoreChange();
    };

    window.addEventListener(CHANGE_EVENT, onStoreChange);
    window.addEventListener("storage", handleStorage);

    return () => {
        window.removeEventListener(CHANGE_EVENT, onStoreChange);
        window.removeEventListener("storage", handleStorage);
    };
}

export interface SavePaymentPreferencesInput {
    down: number;
    term: PaymentLoanTerm;
    tier: PaymentCreditProfileId;
}

export function savePaymentPreferences({ down, term, tier }: SavePaymentPreferencesInput): void {
    const preferences: StoredPaymentPreferences = { down, term, tier, customized: true };

    try {
        window.localStorage.setItem(PAYMENT_PREFERENCES_STORAGE_KEY, JSON.stringify(preferences));
    } catch {
        // Storage can be unavailable (private browsing); cards keep default estimates.
    }

    window.dispatchEvent(new Event(CHANGE_EVENT));
}

/**
 * Shopper payment preferences shared between the shop-by-payment drawer, the
 * VDP pricing panel, and every SRP vehicle card. Saving re-renders card
 * estimates immediately; preferences persist across visits via localStorage.
 */
export function usePaymentPreferences(): {
    preferences: StoredPaymentPreferences | null;
    save: (input: SavePaymentPreferencesInput) => void;
} {
    const preferences = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
    const save = useCallback((input: SavePaymentPreferencesInput) => savePaymentPreferences(input), []);

    return { preferences, save };
}
